import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// Track whether the user is mid-edit so we never hard-reload during typing.
// Components can call `window.__lovableMarkBusy()` while a form is dirty.
declare global {
  interface Window {
    __lovableBusy?: boolean;
    __lovableMarkBusy?: (busy: boolean) => void;
  }
}
window.__lovableBusy = false;
window.__lovableMarkBusy = (busy: boolean) => {
  window.__lovableBusy = busy;
};

// As a safety net, treat any focused text input/textarea/contenteditable as busy.
const isUserActivelyTyping = (): boolean => {
  if (window.__lovableBusy) return true;
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  return false;
};

if ("serviceWorker" in navigator) {
  // Force any already-waiting SW to activate immediately
  const activateWaitingSW = (registration: ServiceWorkerRegistration) => {
    if (registration.waiting) {
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    }
  };

  navigator.serviceWorker.ready.then((registration) => {
    // Activate any SW that is already waiting (from a previous session)
    activateWaitingSW(registration);

    // Check for updates on load, then every 5 minutes (was 10s — far too aggressive
    // and caused mid-typing reloads in admin forms).
    registration.update().catch(() => {});
    setInterval(() => registration.update().catch(() => {}), 5 * 60 * 1000);

    // When a new SW installs, only activate it if the user is idle. Otherwise
    // it will be picked up on the next safe opportunity (visibility change while
    // idle, or next page navigation).
    registration.addEventListener("updatefound", () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && !isUserActivelyTyping()) {
            activateWaitingSW(registration);
          }
        });
      }
    });

    // When a new SW takes control, reload — but ONLY if the user isn't typing.
    // Otherwise defer until the next safe moment.
    let pendingReload = false;
    const safeReload = () => {
      if (isUserActivelyTyping()) {
        pendingReload = true;
        return;
      }
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", safeReload);

    // If a reload was deferred, retry when focus moves off an input.
    document.addEventListener("focusout", () => {
      if (pendingReload && !isUserActivelyTyping()) {
        pendingReload = false;
        window.location.reload();
      }
    });
  }).catch(() => {});

  // Check for updates when returning to the tab — but only activate when idle.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      navigator.serviceWorker.ready.then((reg) => {
        reg.update().catch(() => {});
        if (!isUserActivelyTyping()) {
          activateWaitingSW(reg);
        }
      }).catch(() => {});
    }
  });
}

createRoot(document.getElementById("root")!).render(<App />);
